import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';

import messages from '@broadleaf/admin-components/src/view/components/ApplicationSelector/ApplicationSelector.messages';
import {
  ApplicationCollapsed,
  ApplicationCollapsedProps,
  ApplicationPortrait,
  ApplicationPortraitProps,
  ApplicationSelect,
  ApplicationSelectProps
} from '@broadleaf/admin-components/dist/view/components/ApplicationSelector/components';
import { useUserProfile } from '@broadleaf/admin-components/dist/authentication';
import {
  useCurrentApplication,
  useCurrentTenant,
  useTenantActions
} from '@broadleaf/admin-components/dist/tracking';
import { isAuthorizedForTenantAdmin } from '@broadleaf/admin-components/src/authentication/utils/TenantSecurityUtils';
import {
  setApplication,
  queueFetchApplications
} from '@broadleaf/admin-components/dist/tracking/actions/tenant';
import {
  useDebouncedCallback,
  useFormattedMessages
} from '@broadleaf/admin-components/dist/common';
import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { getApplicationsWithQuery } from '@broadleaf/admin-components/dist/tracking/services/TenantService/TenantService';

/**
 * Override to include the framework fix from https://github.com/BroadleafCommerce/AdminWeb/pull/2637
 */
export const ApplicationSelector: ApplicationSelectorDefinition = ({
  isCollapsed = false
}) => {
  const history = useHistory();
  const location = useLocation();
  const user = useUserProfile();
  const application = useCurrentApplication();
  const tenant = useCurrentTenant();
  const isClearable = isAuthorizedForTenantAdmin(user, tenant);
  const actions = useTenantActions({
    queueFetchApplications,
    setApplication
  });
  const formattedMessages = useFormattedMessages(messages);

  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilteringByQuery, setIsFilteringByQuery] = useState<boolean>(false);

  const handleApplicationChange = useEventCallback(
    nextApplication => {
      actions.setApplication(nextApplication);
      history.push('/', location.state);
    },
    [actions, history.push]
  );

  const resolveApplications = useCallback(
    async (appName?: string) => {
      setIsLoading(true);
      try {
        const response = await getApplicationsWithQuery(
          appName,
          tenant?.id,
          application?.id
        );
        setApplications(response.content);
      } catch (err) {
        if (axios.isCancel(err)) {
          return;
        }
        console.error(
          `Unable to resolve applications for tenant ${tenant?.id} and application ${application?.id}`,
          err
        );
      } finally {
        setIsLoading(false);
      }
    },
    [application?.id, tenant?.id]
  );

  const resolveApplicationsWithQuery = useDebouncedCallback(
    (newValue: string) => {
      setIsFilteringByQuery(!!newValue);
      resolveApplications(newValue);
    },
    50
  );

  useEffect(() => {
    resolveApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isCollapsed) {
    return <ApplicationCollapsed application={application} />;
  }

  return (
    (applications.length !== 0 || isFilteringByQuery || isLoading) && (
      <ApplicationSelect
        application={application}
        applications={applications}
        isClearable={isClearable}
        isLoading={isLoading}
        messages={formattedMessages}
        onChange={handleApplicationChange}
        onFocus={actions.queueFetchApplications}
        onInputChange={resolveApplicationsWithQuery}
      />
    )
  );
};

export interface ApplicationSelectorProps {
  children?: React.ReactNode;

  /** Whether the collapsed component should show */
  isCollapsed?: boolean;
}

export interface ApplicationSelectorDefinition
  extends React.FC<ApplicationSelectorProps> {
  ApplicationCollapsed: React.FC<ApplicationCollapsedProps>;
  ApplicationPortrait: React.FC<ApplicationPortraitProps>;
  ApplicationSelect: React.FC<ApplicationSelectProps>;
}

ApplicationSelector.ApplicationCollapsed = ApplicationCollapsed;
ApplicationSelector.ApplicationPortrait = ApplicationPortrait;
ApplicationSelector.ApplicationSelect = ApplicationSelect;

export default ApplicationSelector;
